import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";

export const COMPANY = {
  legalName: "WattZap Energy Solutions Pvt. Ltd.",
  cin: "U35105TS2026PTC219960",
  address: "H.No. 3-1-322/2, Road No. 7, Mythri Nagar Colony, LB Nagar, Hyderabad, Telangana, India",
  email: "info@wattzap.in",
  phone: "+91 86396 02606",
};

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-heading text-xl text-graphite">{title}</h2>
      <div className="mt-3 text-sm text-slate-600 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  const { lang } = useLang();
  return (
    <div data-testid="privacy-page">
      <section className="bg-[#071A2D] text-white pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#00D4FF] mb-3">Legal</div>
          <h1 className="font-heading text-4xl sm:text-5xl">Privacy Policy</h1>
          <p className="mt-4 text-white/70 text-sm">Last updated: June 2026</p>
        </div>
      </section>
      <section className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          {lang === "te" && (
            <div className="mb-6 rounded-xl bg-mist border border-slate-200 px-4 py-3 text-sm text-slate-700" data-testid="privacy-te-note">
              ఈ గోప్యతా విధానం ఆంగ్లంలో అందించబడింది. ఏవైనా సందేహాలుంటే {COMPANY.email} కు వ్రాయండి — మేము తెలుగులో సహాయం చేస్తాము.
            </div>
          )}
          <p className="text-sm text-slate-600 leading-relaxed">
            This Privacy Policy describes how <strong>{COMPANY.legalName}</strong> (CIN: {COMPANY.cin}), registered office at {COMPANY.address} ("WattZap", "we", "us"),
            collects and processes personal data through this website, in accordance with the Digital Personal Data Protection Act, 2023 ("DPDP Act") and the rules made thereunder.
          </p>

          <Section title="1. Personal data we collect">
            <p>We collect only the personal data you voluntarily provide:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Enquiry forms</strong> (Contact page, Savings Estimator): name, company name, mobile number, email address, monthly electricity bill range or amount, connected load, operating hours, state, and your message.</li>
              <li><strong>AI chat assistant</strong>: the messages you type. We ask you not to share sensitive personal or financial information in chat.</li>
              <li><strong>Guided questionnaire ("WattZap Concierge")</strong>: your facility type, bill range, areas to investigate and state are held only in your browser's temporary session storage and are <strong>not</strong> transmitted to or stored on our servers.</li>
              <li><strong>Usage analytics</strong>: if enabled, aggregated page and interaction analytics (e.g., Google Analytics) to understand how the website is used.</li>
            </ul>
          </Section>

          <Section title="2. Purpose of processing">
            <p>We process your personal data solely to: (a) respond to your enquiry and contact you about your energy-savings requirement via phone, WhatsApp or email; (b) prepare and share your EnergyScan or savings assessment; and (c) improve our website and services. We do not sell personal data, and we do not use it for unrelated third-party marketing.</p>
          </Section>

          <Section title="3. Consent">
            <p>We collect your personal data through the enquiry forms only after you provide free, specific and informed consent by ticking the consent checkbox. You may withdraw your consent at any time by writing to {COMPANY.email}; withdrawal is as easy as giving consent and does not affect the lawfulness of processing done before withdrawal.</p>
          </Section>

          <Section title="4. Sharing and disclosure">
            <p>Your data is accessible only to authorised WattZap personnel. We may use trusted service providers (e.g., cloud hosting, AI processing for summarising enquiries) strictly to deliver the purposes above, under appropriate safeguards. We may disclose data where required by law. Links from this website to the WattZap EnergyScan portal ("WattZap OS") lead to a separate system with its own privacy notice; this website does not transfer your name, contact details or documents to that portal.</p>
          </Section>

          <Section title="5. Retention">
            <p>Enquiry data is retained for as long as needed to serve your enquiry and any resulting engagement, and in any case no longer than 3 years from your last interaction, unless a longer period is required by law. You may request earlier erasure at any time.</p>
          </Section>

          <Section title="6. Security">
            <p>We use reasonable security safeguards — access controls, encrypted transmission (HTTPS) and restricted administrative access — to protect your personal data against unauthorised access, alteration or loss.</p>
          </Section>

          <Section title="7. Your rights">
            <p>Under the DPDP Act you have the right to: access a summary of your personal data and processing activities; correct or update inaccurate data; request erasure; nominate an individual to exercise your rights; withdraw consent; and raise a grievance. To exercise any right, email {COMPANY.email} with the subject "Data Request". We respond within the timelines prescribed by law. If unsatisfied with our response, you may complain to the Data Protection Board of India.</p>
          </Section>

          <Section title="8. Grievance contact">
            <p>
              Grievance / privacy contact: <strong>{COMPANY.legalName}</strong><br />
              Email: <a href={`mailto:${COMPANY.email}`} className="text-[#0092b0] underline">{COMPANY.email}</a> · Phone/WhatsApp: {COMPANY.phone}<br />
              Registered office: {COMPANY.address}
            </p>
          </Section>

          <Section title="9. Changes to this policy">
            <p>We may update this policy from time to time. The "Last updated" date above reflects the latest version. Material changes will be highlighted on this page.</p>
          </Section>

          <div className="mt-10 text-sm">
            <Link to="/terms" className="text-[#0092b0] underline" data-testid="privacy-to-terms">Read our Terms of Use →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
